import { LineGame } from './LineGame.js';
import { LineRenderer } from './LineRenderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { BaseInput, InputCallback } from '../../input/BaseInput.js';
import { Point, pointsEqual } from '../../core/GridGeometry.js';
import { KeyboardControllable, FinePoint } from '../../input/KeyboardController.js';

/** The two intersections a half-grid edge point lies between, or null off an edge. */
function edgeEndpoints(p: FinePoint): [Point, Point] | null {
    const rOdd = p.r % 2 === 1;
    const cOdd = p.c % 2 === 1;
    if (rOdd === cOdd) return null; // a center (both odd) or an intersection (both even)
    return rOdd
        ? [{ row: (p.r - 1) / 2, col: p.c / 2 }, { row: (p.r + 1) / 2, col: p.c / 2 }]  // vertical
        : [{ row: p.r / 2, col: (p.c - 1) / 2 }, { row: p.r / 2, col: (p.c + 1) / 2 }]; // horizontal
}

/** A half-grid intersection point as a grid intersection, or null off an intersection. */
function fineIntersection(p: FinePoint): Point | null {
    return p.r % 2 === 0 && p.c % 2 === 0 ? { row: p.r / 2, col: p.c / 2 } : null;
}

export class LineInput extends BaseInput<LineGame, LineRenderer> implements KeyboardControllable {
    private gateMode: boolean = false;

    constructor(
        game: LineGame,
        renderer: LineRenderer,
        canvas: HTMLCanvasElement,
        onUpdate: InputCallback
    ) {
        super(game, renderer, canvas, onUpdate);
        this.setupEventListeners();
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    protected setupEventListeners(): void {
        this.canvas.addEventListener('click', this.handleClick);
        this.canvas.addEventListener('contextmenu', this.handleRightClick);
    }

    protected removeEventListeners(): void {
        this.canvas.removeEventListener('click', this.handleClick);
        this.canvas.removeEventListener('contextmenu', this.handleRightClick);
    }

    private handleRightClick = (event: MouseEvent): void => {
        event.preventDefault();
        if (this.gateMode) return;
        const { x, y } = this.getCoordinates(event);
        const edge = this.detector.detectEdge(x, y);
        if (edge && this.game.hasSegment(edge.pa, edge.pb)) {
            this.game.toggleSegment(edge.pa, edge.pb);
            this.onUpdate();
        }
    };

    private handleClick = (e: MouseEvent): void => {
        const { x, y } = this.getCoordinates(e);

        // Gate mode: detect cell click for gate toggling
        if (this.gateMode && isVortexGame(this.game)) {
            const hit = this.detector.detectCell(x, y);
            if (hit && this.game.toggleVortexGate(hit)) {
                this.onUpdate();
            }
            return;
        }

        const intersection = this.detector.detectIntersection(x, y);
        if (intersection) {
            // Convenience: clicking the intersection a hint chevron points to lays
            // that segment (from the tip toward it), so the hint is directly tappable.
            // Gated on hints being shown, so it never fires without a visible chevron.
            if (this.renderer.getHintsEnabled()) {
                const hint = this.game.getSegmentHints().find(h => pointsEqual(h.to, intersection));
                if (hint && this.game.toggleSegment(hint.from, hint.to)) {
                    this.onUpdate();
                    return;
                }
            }

            // Otherwise a rim intersection sets/moves the start point.
            if (this.game.isOnGridEdge(intersection) && this.game.setStartPoint(intersection)) {
                this.onUpdate();
                return;
            }
        }

        // Otherwise, check if clicking on an edge (for segment toggle)
        const edge = this.detector.detectEdge(x, y);
        if (edge) {
            if (this.game.toggleSegment(edge.pa, edge.pb)) {
                this.onUpdate();
            }
        }
    };

    // ── Keyboard mode ─────────────────────────────────────────────────
    // The line runs along grid lines, so — like Flood — the cursor rides the
    // half-grid and the trace anchors on intersections. A tap on an *edge* toggles
    // that one segment (the direct edge-toggle the mouse has); the trace toggles a
    // segment per hop, two at once with a wrap across the rim (`tracesToroidal`). A
    // tap on a rim intersection sets the line start; C clears the segments meeting a
    // point (or the one edge under the cursor).

    usesFineCursor(): boolean {
        return true;
    }

    /** The line runs to the rim, and its start sits on a rim intersection. */
    allowsFineRim(): boolean {
        return true;
    }

    /** The cursor wraps like the line does. Because the rim is a legal resting place
     *  (`allowsFineRim`), a step off the edge lands *on* the rim first and only the
     *  next one crosses to the far rim — which is exactly the line's own wrap. */
    wrapsFineCursor(): boolean {
        return true;
    }

    /** Segments run along the grid lines, so the trace anchors on intersections… */
    tracesFineIntersections(): boolean {
        return true;
    }

    /** …and wraps toroidally, toggling the mirror segment across the rim. */
    tracesToroidal(): boolean {
        return true;
    }

    /** Used by the toroidal trace (controller) to toggle the segment it crosses. */
    toggleSegment(a: Point, b: Point): boolean {
        if (this.gateMode) return false;
        return this.game.toggleSegment(a, b);
    }

    /** Lets the toroidal trace tell an erase from an extend at the rim. */
    hasSegment(a: Point, b: Point): boolean {
        return this.game.hasSegment(a, b);
    }

    /** The wrap's stopping case: when a rim hop brings the line home, don't mirror. */
    traceReachesEnd(): boolean {
        return this.game.trace().reachedEnd;
    }

    placeAtFine(p: FinePoint): boolean {
        if (this.gateMode) return false;

        // An edge: toggle that one segment directly (what the mouse does on an edge).
        const ends = edgeEndpoints(p);
        if (ends) return this.game.toggleSegment(ends[0], ends[1]);

        // A rim intersection: set / move the line start.
        const pt = fineIntersection(p);
        return !!pt && this.game.isOnGridEdge(pt) && this.game.setStartPoint(pt);
    }

    deleteAtFine(p: FinePoint): boolean {
        if (this.gateMode) return false;

        // On an edge: remove just that segment.
        const ends = edgeEndpoints(p);
        if (ends) {
            if (!this.game.hasSegment(ends[0], ends[1])) return false;
            return this.game.toggleSegment(ends[0], ends[1]);
        }

        // On an intersection: clear every segment meeting it.
        const pt = fineIntersection(p);
        if (!pt) return false;
        let changed = false;
        for (const [dr, dc] of [[-1, 0], [1, 0], [0, -1], [0, 1]] as const) {
            const nb: Point = { row: pt.row + dr, col: pt.col + dc };
            if (this.game.getGrid().isValidPoint(nb) && this.game.hasSegment(pt, nb)) {
                this.game.toggleSegment(pt, nb);
                changed = true;
            }
        }
        return changed;
    }

    /** `X` goes to the line's start point, or — before one is set — to the rim
     *  intersection nearest the first digit. */
    fineCursorOrigin(): FinePoint | null {
        const pt = this.originPoint();
        return pt ? { r: 2 * pt.row, c: 2 * pt.col } : null;
    }

    private originPoint(): Point | null {
        const start = this.game.getStartPoint();
        if (start) return { ...start };

        const grid = this.game.getGrid();
        const d = grid.getDigitPosition(grid.minDigit);
        if (!d) return null;
        // Measure to the digit cell's middle, so the nearest rim point is the one
        // actually facing it rather than one of its corners.
        const target = { row: d.row + 0.5, col: d.col + 0.5 };
        const rim = grid.rimPoints();
        if (rim.length === 0) return null;
        const dist = (p: { row: number; col: number }) =>
            Math.hypot(p.row - target.row, p.col - target.col);
        return rim.reduce((a, b) => (dist(b) < dist(a) ? b : a));
    }
}
